import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useCrypto } from '@/infra/crypto'
import { toEncryptedPayload } from '@/infra/crypto/payloads'
import { hostProjectsClient } from './hostProjectsClient'
import { HOST_PROJECTS_QUERY_KEYS } from './queryKeys'
import { isExcludedProject } from './projectExclusions'
import type { DecryptedProject, ProjectDiscoveryCache, ProjectEntry } from './types'

interface UseHostProjectsReturn {
  readonly projects: readonly DecryptedProject[]
  readonly isLoading: boolean
  readonly error: string | null
  readonly refetch: () => void
}

export function useHostProjects(hostId: string | null): UseHostProjectsReturn {
  const { decrypt, isReady: cryptoReady } = useCrypto()

  const query = useQuery({
    queryKey: HOST_PROJECTS_QUERY_KEYS.byHost(hostId ?? ''),
    queryFn: async () => {
      if (!hostId) throw new Error('Host ID is required')

      const result = await hostProjectsClient.fetchHostProjects(hostId)
      if (!result.success) {
        if (result.error === 'not_found') return []
        throw new Error(result.message)
      }

      const { hostProjects, encryption } = result.data
      const payload = toEncryptedPayload(hostProjects, encryption.keyVersion)
      const decrypted = await decrypt(payload)

      const cache = parseDiscoveryCache(decrypted)
      const projects = mergeAndNormalize(cache)
      return sortProjects(projects)
    },
    enabled: hostId !== null && hostId.length > 0 && cryptoReady,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  })

  const projects = useMemo(() => query.data ?? [], [query.data])

  return {
    projects,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : null,
    refetch: () => {
      void query.refetch()
    },
  }
}

function parseDiscoveryCache(decrypted: unknown): ProjectDiscoveryCache {
  const obj = typeof decrypted === 'string' ? (JSON.parse(decrypted) as unknown) : decrypted

  if (
    typeof obj !== 'object' ||
    obj === null ||
    !Array.isArray((obj as Record<string, unknown>).code_repositories) ||
    !Array.isArray((obj as Record<string, unknown>).projects)
  ) {
    throw new Error('Malformed project discovery cache')
  }

  return obj as ProjectDiscoveryCache
}

function toDecryptedProject(entry: ProjectEntry): DecryptedProject {
  return {
    name: entry.name,
    path: entry.path,
    parentFolder: entry.parent_folder,
    discoveredAt: entry.discovered_at,
  }
}

function mergeAndNormalize(cache: ProjectDiscoveryCache): readonly DecryptedProject[] {
  const seen = new Set<string>()
  const results: DecryptedProject[] = []

  for (const entry of cache.code_repositories) {
    if (!seen.has(entry.path) && !isExcludedProject(entry.path)) {
      seen.add(entry.path)
      results.push(toDecryptedProject(entry))
    }
  }

  for (const entry of cache.projects) {
    if (!seen.has(entry.path) && !isExcludedProject(entry.path)) {
      seen.add(entry.path)
      results.push(toDecryptedProject(entry))
    }
  }

  return results
}

function sortProjects(projects: readonly DecryptedProject[]): readonly DecryptedProject[] {
  return [...projects].sort((a, b) => {
    const dateComparison = b.discoveredAt - a.discoveredAt
    if (dateComparison !== 0) return dateComparison
    return a.path.localeCompare(b.path)
  })
}
